/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, MutableRefObject, Reducer, useMemo, useReducer, useRef } from 'react';

// This file is partially adapted from this lib:
// https://www.npmjs.com/package/action-hook
//
// Original author: sloth9725
// License: MIT
export type Action<Payload = void, Type = string> = {
  type: Type;
  payload: Payload;
};

type ReducerFunction<State = any> = (state: State, payload?: any) => State;

interface ReducersInterface<State = any> {
  [key: string]: ReducerFunction<State>;
}

type PayloadType<R extends ReducerFunction> = Parameters<R>[1];

type ActionsInterface<State, R extends ReducersInterface<State>> = {
  [K in keyof R]: PayloadType<R[K]> extends undefined
    ? () => Promise<State>
    : (arg: PayloadType<R[K]>['payload']) => Promise<State>;
};

/**
 * Creates a reducer function from passed-in handlers
 *
 * INTERNAL ONLY
 *
 * @param initialState Initial state of the reducer
 * @param handlers Handler functions (cases in typical reducers)
 * @param stateRef Reference object containing the state
 */
const createReducer =
  <State, Reducers extends ReducersInterface = ReducersInterface<State>>(
    initialState: State,
    handlers: Reducers,
    stateRef: MutableRefObject<State>,
  ): Reducer<State, Reducers> =>
  (state = initialState, action: Parameters<Reducers[string]>[1] = undefined) => {
    if (Object.hasOwn(handlers, action.type)) {
      stateRef.current = handlers[action.type](state, action);

      if (action.onReducerFinished) {
        action.onReducerFinished(stateRef.current);
      }

      return stateRef.current;
    }

    return state;
  };

/**
 * Creates action creator functions bound to dispatch call
 *
 * INTERNAL ONLY
 *
 * @param handlers Handler functions (cases in typical reducers)
 * @param dispatch Dispatch function from useReducer hook
 */
const createActions = <State, Reducers extends ReducersInterface = ReducersInterface<State>>(
  handlers: Reducers,
  dispatch: Dispatch<Parameters<Reducers[string]>[1]>,
) =>
  Object.getOwnPropertyNames(handlers).reduce((newActions, handlerName) => {
    const actionType = handlerName as keyof Reducers;

    newActions[actionType] = (payload: PayloadType<Reducers[typeof actionType]>['payload'] = undefined) => {
      let onReducerFinished: (state: State) => void = () => {};

      const promise = new Promise<State>((resolve) => {
        onReducerFinished = resolve;
      });

      dispatch({
        type: actionType,
        payload,
        onReducerFinished,
      });

      return promise;
    };

    return newActions;
  }, {} as ActionsInterface<State, Reducers>);

const defaultMapState = <State, ComputedState = State>(state: State): ComputedState =>
  state as unknown as ComputedState;

/**
 * Enhanced useReducer hook that utilizes ideas from Redux Toolkit for reducer definitions.
 *
 * @param initialState Initial state of the reducer
 * @param reducers Reducers (handlers)
 * @param mapState Optional computed state handler
 */
const useEnhancedReducer = <
  State,
  Reducers extends ReducersInterface = ReducersInterface<State>,
  ComputedState = State,
>(
  initialState: State,
  reducers: Reducers,
  mapState: (value: State) => ComputedState = defaultMapState,
) => {
  const stateRef = useRef<State>(initialState);
  const reducer = useMemo(() => createReducer(initialState, reducers, stateRef), []);
  const [state, dispatch] = useReducer(reducer, initialState);
  const actions = useMemo(() => createActions<State, Reducers>(reducers, dispatch), [dispatch]);
  const computedState = useMemo(() => mapState(state), [state, mapState]);

  return [computedState, actions, stateRef, dispatch] as const;
};

export default useEnhancedReducer;
